import { useEffect, useMemo } from "react";
import { Exercise, Variants } from "types";
import useQuery from "hooks/useQuery";
import ExerciseMetadataService from "services/ExerciseMetadataService";
import PersonalBestService from "services/PersonalBestService";
import { useSWRConfig } from "swr";

interface UseIsPbCheckerParams {
  athleteId: string;
  exerciseId: string;
  exercise: Exercise | undefined;
  variant: Variants | null;
  value: number | undefined;
  date: string;
  measurementId: string;
}

/**
 * Module-level tracking of latest checked measurement dates per cache key.
 * Used to determine if we should invalidate the cache and refetch.
 */
const latestCheckedDates = new Map<string, string>();

/**
 * Hook to check if a measurement is a personal best.
 *
 * Uses SWR caching with conditional revalidation:
 * - Cache key: [athleteId, exerciseId, variant]
 * - Only revalidates when measurement date is newer than last checked date
 * - De-duplicates concurrent requests via SWR's dedupingInterval
 */
function useIsPbChecker(params: UseIsPbCheckerParams): boolean {
  const { athleteId, exerciseId, exercise, variant, value, date } = params;
  const { mutate } = useSWRConfig();

  const defaultMetric = useMemo(() => {
    return exercise ? ExerciseMetadataService.defaultMetric(exercise) : null;
  }, [exercise]);

  const cacheKey = useMemo(() => {
    if (!defaultMetric?.field) return null;
    return ["PersonalBestService.getOne", athleteId, exerciseId, variant ?? "null", defaultMetric.field] as const;
  }, [athleteId, exerciseId, variant, defaultMetric?.field]);

  const cacheKeyString = useMemo(() => (cacheKey ? JSON.stringify(cacheKey) : null), [cacheKey]);

  // Fetch PB data with SWR
  const { data: pbData } = useQuery(
    cacheKey,
    async ([, aId, eId, v, metricId]) => {
      return PersonalBestService.getOne(aId, {
        exerciseId: eId,
        metricId,
        sortType: defaultMetric?.sortType ?? 1,
        variant: v === "null" ? null : (v as Variants),
      });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: 5000,
      refreshInterval: 60000,
    }
  );

  // Conditionally invalidate cache when measurement date is newer
  useEffect(() => {
    if (!cacheKeyString || !cacheKey) return;

    const lastChecked = latestCheckedDates.get(cacheKeyString);

    if (!lastChecked || date > lastChecked) {
      latestCheckedDates.set(cacheKeyString, date);

      // If we already have data and this is a newer measurement, revalidate
      if (lastChecked) {
        mutate(cacheKey);
      }
    }
  }, [cacheKeyString, cacheKey, date, mutate]);

  // Determine if this measurement is the PB
  return useMemo(() => {
    if (!pbData || typeof value !== "number" || !pbData.date) return false;

    return params.measurementId === pbData.id;
  }, [pbData, value, params.measurementId]);
}

export default useIsPbChecker;
